// @ts-strict-ignore
import cloneDeep from 'lodash/cloneDeep';
import { getProperty, setProperty } from 'dot-prop';

import type {
  BooleanRule,
  IndexedRule,
  Rules,
} from '../../types';
import { RuleUpdateErrorType } from '../constants';
import { getOperatorUsedInRuleObject, getPathArrayAndParentPathString } from '../helpers';
import { WorkflowEngineException } from '../../schema';
import { getNumericPathIndices } from '~/dashboard/views/workflows/service/actions';
import { createStringOfPathArray } from '../../utils';

function getGroupOpeningPath(openingParenthesisPath: string) {
  // by design, rulePathIndices (which is generated using parentRulePath) is going to point to leftAction part, which is one level deeper than where the group should open
  const groupOpeningPathIndices = getNumericPathIndices(openingParenthesisPath, 'rules');
  return createStringOfPathArray(
    groupOpeningPathIndices.slice(0, groupOpeningPathIndices.length - 1),
  );
}

export function getAllPossibleThresholdsForGrouping({
  booleanRule,
  openingParenthesisPath,
}: {
  booleanRule: Rules;
  openingParenthesisPath: string;
}) {
  let groupOpeningPath = getGroupOpeningPath(openingParenthesisPath);
  // if the path doesn't end with array index position (e.g. or[2])
  // it's a wrong path because we need to know the position in an array of the ruleset
  // in order to group everything inbetween indexA (the last "index" in this path string) to indexB (which later user will confirm) basically
  if (openingParenthesisPath === ''
    && typeof booleanRule === 'object'
    && 'id' in booleanRule
  ) {
    return RuleUpdateErrorType.NOT_GROUPABLE_SCOPE;
  }

  if (!groupOpeningPath.endsWith(']')) {
    const pathData = getProperty(
      booleanRule,
      openingParenthesisPath,
    ) as (BooleanRule | IndexedRule);
    const ruleOperatorOfPathData = getOperatorUsedInRuleObject({ rule: pathData });
    if (ruleOperatorOfPathData) {
      groupOpeningPath = openingParenthesisPath;
    } else {
      throw new WorkflowEngineException('', RuleUpdateErrorType.INCORRECT_PATH);
    }
  }

  const {
    parentPathString,
    lastPathItem,
  } = getPathArrayAndParentPathString({
    pathString: groupOpeningPath.slice(),
    parentLevel: 1,
  });

  const parentPathData = cloneDeep(getProperty(
    booleanRule,
    parentPathString,
  )) as (BooleanRule | IndexedRule)[];

  if (!Array.isArray(parentPathData)) {
    return RuleUpdateErrorType.NOT_GROUPABLE_SCOPE;
  }

  return parentPathData.slice(lastPathItem);
}

export function groupRuleItems({
  originalRule,
  openingParenthesisPath,
  closingParenthesisPath,
}: {
  originalRule: Rules | {};
  openingParenthesisPath: string;
  closingParenthesisPath: string;
}) {
  const booleanRule = cloneDeep(originalRule);
  if (typeof booleanRule === 'string' || 'id' in booleanRule) {
    throw new WorkflowEngineException('', RuleUpdateErrorType.NOT_GROUPABLE_SCOPE);
  }

  let groupOpeningPath = getGroupOpeningPath(openingParenthesisPath);
  // if the path doesn't end with array index position (e.g. or[2])
  // it's a wrong path because we need to know the position in an array of the ruleset
  // in order to group everything inbetween indexA (openingParenthesisPath) to indexB (closingParenthesisPath)
  if (!groupOpeningPath.endsWith(']') || !closingParenthesisPath.endsWith(']')) {
    const pathData = getProperty(
      booleanRule,
      !groupOpeningPath.endsWith(']')
        ? openingParenthesisPath
        : groupOpeningPath,
    ) as (BooleanRule | IndexedRule);
    const ruleOperatorOfPathData = getOperatorUsedInRuleObject({ rule: pathData });
    if (ruleOperatorOfPathData) {
      groupOpeningPath = openingParenthesisPath;
    } else {
      throw new WorkflowEngineException('', RuleUpdateErrorType.INCORRECT_PATH);
    }
  }

  const {
    parentPathString: openingParentPathString,
    lastPathItem: openingPathLastItem,
  } = getPathArrayAndParentPathString({
    pathString: groupOpeningPath.slice(),
    parentLevel: 1,
  });

  const {
    lastPathItem: closingPathLastItem,
  } = getPathArrayAndParentPathString({
    pathString: closingParenthesisPath.slice(),
    parentLevel: 1,
  });

  const parentPathData = getProperty(
    booleanRule,
    openingParentPathString,
  ) as (BooleanRule | IndexedRule)[];

  if (!Array.isArray(parentPathData)) {
    return RuleUpdateErrorType.NOT_GROUPABLE_SCOPE;
  }

  // in the parent array data, we basically grab everything positioned "after"
  // the current item in the parent array
  const itemsInBetweenParentheses = parentPathData
    .slice(openingPathLastItem, closingPathLastItem + 1);

  let newOperatorForNewlyFormingGroup;
  if (openingParentPathString.endsWith('and')) {
    newOperatorForNewlyFormingGroup = 'and';
  }

  if (openingParentPathString.endsWith('or')) {
    newOperatorForNewlyFormingGroup = 'or';
  }

  // if there are any of those, now it'll combine them into 1 single rule group.
  // say user set parentheses for 1 threshold id, 1 "or" group (, and another 1 "or" group in some cases) that live under "and" ruleset
  // in that case, creating a new "or" group for all of them results in multiple nested "or" operator groups
  // which is pretty pointless. this also may end up causing an issue if they want to flip operator
  // so we combine them into one as that's most logical here.

  const newGroup = {
    [newOperatorForNewlyFormingGroup]: [],
  };
  itemsInBetweenParentheses.forEach(item => {
    newGroup[newOperatorForNewlyFormingGroup] = [
      ...newGroup[newOperatorForNewlyFormingGroup],
      item,
    ];
  });

  // now we overwrite the item that was placed at the opening parenthesis - with the new group
  setProperty(
    booleanRule,
    groupOpeningPath,
    newGroup,
  );

  // and now we remove the rest of items that were in the boolean rule
  // that were "moved" into the new group
  const deleteCount = Number(closingPathLastItem + 1) - Number(openingPathLastItem);
  parentPathData.splice(openingPathLastItem + 1, deleteCount - 1);

  // write the altered parent data
  setProperty(
    booleanRule,
    openingParentPathString,
    parentPathData,
  );

  return booleanRule;
}

export function ungroupRuleItems({
  originalRule,
  groupParenthesisPath,
}: {
  originalRule: Rules | {};
  groupParenthesisPath: string;
}) {
  const booleanRule = cloneDeep(originalRule);
  if (typeof booleanRule === 'string' || 'id' in booleanRule) {
    throw new WorkflowEngineException('', RuleUpdateErrorType.NOT_GROUPABLE_SCOPE);
  }

  // if the path doesn't end with array index position (e.g. or[2])
  // it's a wrong path because we need to know the position in an array of the ruleset
  // in order to detect the rule set group as a whole
  if (!groupParenthesisPath.endsWith(']')) {
    throw new WorkflowEngineException('', RuleUpdateErrorType.INCORRECT_PATH);
  }

  const pathData = cloneDeep(getProperty(
    booleanRule,
    groupParenthesisPath,
  )) as (BooleanRule | IndexedRule);

  const operatorOfPathData = getOperatorUsedInRuleObject({
    rule: pathData,
  });

  const {
    parentPathString,
    lastPathItem,
  } = getPathArrayAndParentPathString({
    pathString: groupParenthesisPath.slice(),
    parentLevel: 1,
  });

  const parentPathData = getProperty(
    booleanRule,
    parentPathString,
  ) as (BooleanRule | IndexedRule)[];

  if (!Array.isArray(parentPathData)) {
    return RuleUpdateErrorType.NOT_UNGROUPABLE_SCOPE;
  }

  const updatedParentPathData = [
    ...parentPathData.slice(0, lastPathItem),
    ...pathData[operatorOfPathData as string],
    ...parentPathData.slice(lastPathItem + 1),
  ];

  setProperty(
    booleanRule,
    parentPathString,
    updatedParentPathData,
  );

  return booleanRule;
}
